import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Campo, Departamento, Pestania, ProcedimientoDinamico } from 'src/app/model/procedimiendo-dinamico';
import { ServicioGeneral } from 'src/app/_services/servicio-general.service';
import { NotificationService } from 'src/app/_services/notification.service';
import { ProcedimientosService } from 'src/app/_services/procedimientos.service';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

export type BuilderStep = 0 | 1 | 2;

export type PreviewMode = 'form' | 'mobile' | 'json';

interface Selection {
  pestaniaIndex: number | null;
  campoIndex: number | null;
}

@Component({
  selector: 'app-form-builder-tramite',
  templateUrl: './form-builder-tramite.component.html',
  styleUrls: ['./form-builder-tramite.component.scss']
})
export class FormBuilderTramiteComponent implements OnInit, OnChanges {

  @Input() editingId: number | null = null;
  @Output() close = new EventEmitter<{ saved: boolean }>();

  readonly ocultarVisible = environment.environment === 'gmu';

  currentStep: BuilderStep = 0;
  previewMode: PreviewMode = 'form';
  selection: Selection = { pestaniaIndex: null, campoIndex: null };

  tramiteForm: FormGroup;

  departamentos: Departamento[] = [];

  get stepLabels(): string[] {
    return [
      this.translate.instant('FORM_BUILDER.STEP.DATOS'),
      this.translate.instant('FORM_BUILDER.STEP.PESTANAS'),
      this.translate.instant('FORM_BUILDER.STEP.REVISAR'),
    ];
  }

  get stepLabel(): string {
    return this.stepLabels[this.currentStep] ?? '';
  }

  readonly tiposCampo = [
    { value: 'text',        icon: 'edit' },
    { value: 'number',      icon: 'looks_one' },
    { value: 'email',       icon: 'email' },
    { value: 'date',        icon: 'event' },
    { value: 'select',      icon: 'arrow_drop_down' },
    { value: 'multiselect', icon: 'check_box' },
    { value: 'checkbox',    icon: 'check_box_outline_blank' },
    { value: 'textarea',    icon: 'notes' },
    { value: 'file',        icon: 'insert_drive_file' }
  ];
  readonly validadores = [
    { value: '' },
    { value: 'DNI/NIE' },
    { value: 'Email' },
    { value: 'Telefono' },
    { value: 'IBAN' },
    { value: 'Pattern' },
    { value: 'Entero' },
    { value: 'Decimal' }
  ];

  readonly divClassOptions = [
    { value: 'col-sm-12', key: 'FULL' },
    { value: 'col-sm-6',  key: 'HALF' },
    { value: 'col-sm-4',  key: 'THIRD' },
    { value: 'col-sm-3',  key: 'QUARTER' }
  ];

  expandedPestanias = new Set<number>();
  nuevaOpcion = { valor: '', label: '' };

  /** Códigos de listados ya existentes en BD para que el admin los reutilice. */
  listadosDisponibles: string[] = [];

  previewActiveTabIndex = 0;
  previewExpanded = false;
  jsonCopied = false;

  saving = false;
  saved = false;

  /**
   * Ids (BD) de pestañas y campos que el admin ha eliminado del builder.
   * Se dan de baja en el back al pulsar «Guardar trámite»; hasta entonces el
   * borrado es solo local y cancelable cerrando sin guardar.
   */
  private pestaniasEliminadasIds: number[] = [];
  private camposEliminadosIds: number[] = [];

  constructor(
    private fb: FormBuilder,
    private servicioGeneral: ServicioGeneral,
    private notificationService: NotificationService,
    private procedimientosService: ProcedimientosService,
    private translate: TranslateService
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  validatorLabel(v: { value: string }): string {
    const map: Record<string, string> = {
      '': 'NONE',
      'DNI/NIE': 'DNI_NIE',
      Email: 'EMAIL',
      Telefono: 'TELEFONO',
      IBAN: 'IBAN',
      Pattern: 'PATTERN',
      Entero: 'ENTERO',
      Decimal: 'DECIMAL'
    };
    return this.t('FORM_BUILDER.VALIDATOR_OPT.' + (map[v.value] ?? 'NONE'));
  }

  ngOnInit(): void {
    this.tramiteForm = this.fb.group({
      id: [null],
      nombre: ['', Validators.required],
      codigo: ['', Validators.required],
      sia: [null],
      identificador: [null],
      visible: [false],
      link_ayuda: [''],
      link_ordenanza: [''],
      fechaApertura: [''],
      fechaCierre: [''],
      departamento: this.fb.group({
        id: [null, Validators.required]
      }),
      custom: [true],
      pestanias: this.fb.array([])
    });

    this.loadDepartamentos();
    this.loadListadosDisponibles();

    if (this.editingId) {
      this.loadProcedimiento(this.editingId);
    }
  }

  /**
   * Carga la lista de códigos de catálogo (campo `lista` en la tabla legacy
   * `opciones_listados_procedimiento_custom`) que ya existen en BD. Permite
   * al admin escoger uno desde el selector en lugar de teclearlo a mano.
   */
  private loadListadosDisponibles(): void {
    this.procedimientosService.getListasDisponibles().subscribe({
      next: (resp: any) => {
        const arr = Array.isArray(resp) ? resp : (resp?.listas ?? []);
        this.listadosDisponibles = (arr as string[])
          .filter(s => !!s && s.trim().length > 0)
          .sort((a, b) => a.localeCompare(b));
      },
      error: () => {
        this.listadosDisponibles = [];
      }
    });
  }

  /**
   * Llamado cuando el admin selecciona un listado existente. Sustituye el
   * contenido actual del `FormArray` de opciones por las opciones reales del
   * catálogo elegido. Si pasa cadena vacía, deja el campo libre para que el
   * admin defina opciones manualmente (modo "crear nuevo").
   */
  onListadoExistenteSelected(codigo: string): void {
    const campo = this.selectedCampo;
    if (!campo) return;
    campo.get('listado')?.setValue(codigo || '');
    // Sincroniza listadoSeleccionado con el nuevo código para que el back-end
    // entre en la rama de UPDATE (compara opciones por idBaseDatos) y no en
    // la rama de CREATE (que insertaría duplicados sobre un catálogo
    // existente).
    campo.get('listadoSeleccionado')?.setValue(codigo || '');

    const opcionesArr = campo.get('opciones') as FormArray;
    opcionesArr.clear();
    if (!codigo) return;

    this.procedimientosService.getListaByLista(codigo).subscribe({
      next: (resp: any) => {
        const list: any[] = Array.isArray(resp) ? resp : (resp?.listado ?? []);
        list
          .filter(op => op && (op.id != null || op.valor || op.nombre))
          .forEach(op => {
            opcionesArr.push(this.fb.group({
              id: [null],
              valor: [op.valor ?? ''],
              label: [op.nombre ?? ''],
              idBaseDatos: [op.id ?? null]
            }));
          });
      },
      error: () => {
        this.notificationService.error(
          this.t('COMMON.ERROR'),
          this.t('FORM_BUILDER.MSG.LIST_FAIL'),
          3000
        );
      }
    });
  }

  /**
   * Indica si el valor actual del campo `listado` coincide con un código
   * existente en BD. Sirve para resaltar en el `<select>` la opción correcta.
   */
  isListadoExistente(codigo: string | null | undefined): boolean {
    if (!codigo) return false;
    return this.listadosDisponibles.includes(codigo);
  }

  /**
   * Busca campos tipo select/multiselect sin listado asignado.
   * Devuelve lista de [pestañaIdx, campoIdx] para diagnóstico.
   */
  private findCamposSelectSinListado(): Array<{ p: number; c: number }> {
    const out: Array<{ p: number; c: number }> = [];
    this.pestanias.controls.forEach((pest, p) => {
      const campos = (pest as FormGroup).get('campos') as FormArray | null;
      if (!campos) return;
      campos.controls.forEach((campo, c) => {
        const tipo = (campo as FormGroup).get('tipo')?.value;
        if (!this.showOpciones(tipo)) return;
        const listado = ((campo as FormGroup).get('listado')?.value || '').trim();
        if (!listado) out.push({ p, c });
      });
    });
    return out;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingId'] && !changes['editingId'].firstChange) {
      if (this.editingId) this.loadProcedimiento(this.editingId);
    }
  }

  private loadProcedimiento(id: number): void {
    this.procedimientosService.getProcedimiento(id).subscribe({
      next: (data: any) => {
        const proc = data?.['Procedimiento'];
        if (!proc) {
          this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.LOAD_FAIL'), 5000);
          return;
        }
        this.seedFormFromProcedimiento(proc);
      },
      error: () => {
        this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.LOAD_FAIL'), 5000);
      }
    });
  }

  private seedFormFromProcedimiento(proc: any): void {
    // limpiar pestanias previas
    while (this.pestanias.length) this.pestanias.removeAt(0);
    this.pestaniasEliminadasIds = [];
    this.camposEliminadosIds = [];

    this.tramiteForm.patchValue({
      id: proc.id,
      nombre: proc.nombre ?? '',
      codigo: proc.codigo ?? '',
      sia: proc.sia ?? null,
      identificador: proc.identificador ?? null,
      visible: !!proc.visible,
      link_ayuda: proc.link_ayuda ?? '',
      link_ordenanza: proc.link_ordenanza ?? '',
      fechaApertura: proc.fechaApertura ?? '',
      fechaCierre: proc.fechaCierre ?? '',
      departamento: { id: proc.departamento?.id ?? null },
      custom: proc.custom ?? true
    });

    const pestaniasOrdenadas = [...(proc.pestanias ?? [])]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

    pestaniasOrdenadas.forEach(p => this.pestanias.push(this.buildPestaniaGroup(p)));
  }

  /**
   * Construye el FormGroup de una pestaña (con sus campos) a partir del JSON
   * que devuelve el back en /procedimiento/get/{id}. Con stripIds=true los ids
   * de pestaña y campos se ponen a null, de modo que al guardar se creen como
   * nuevos (se usa al copiar una pestaña desde otro trámite).
   */
  private buildPestaniaGroup(p: any, stripIds: boolean = false): FormGroup {
    const pestGroup = this.fb.group({
      id: [stripIds ? null : (p.id ?? null)],
      nombre: [p.nombre ?? '', Validators.required],
      titulo: [p.titulo ?? ''],
      tagId: [p.tagId ?? '', [Validators.required, Validators.pattern('^[A-z0-9_]{1,255}$')]],
      orden: [p.orden ?? 1, Validators.required],
      visible: [p.visible ?? true],
      campos: this.fb.array([])
    });
    const camposArr = pestGroup.get('campos') as FormArray;
    const camposOrdenados = [...(p.campos ?? [])]
      .sort((a: any, b: any) => (a.orden ?? 0) - (b.orden ?? 0));
    camposOrdenados.forEach((c: any) => camposArr.push(this.buildCampoGroup(c, stripIds)));
    return pestGroup;
  }

  private buildCampoGroup(c: any, stripIds: boolean = false): FormGroup {
    const opciones = this.fb.array(
      (c.opcionesListado ?? []).map((op: any) => this.fb.group({
        id: [op.id ?? null],
        valor: [op.id ?? ''],
        label: [op.label ?? ''],
        idBaseDatos: [op.idBaseDatos ?? null]
      }))
    );
    return this.fb.group({
      id: [stripIds ? null : (c.id ?? null)],
      tipo: [c.tipo ?? 'text', Validators.required],
      label: [c.label ?? ''],
      textoRepresentativo: [c.textoRepresentativo ?? ''],
      nombre: [c.nombre ?? '', Validators.required],
      valor: [c.valor ?? ''],
      orden: [c.orden ?? 1, Validators.required],
      visible: [c.visible ?? true],
      habilitado: [c.habilitado ?? true],
      tipovalidador: [c.tipovalidador ?? ''],
      patternvalidador: [c.patternvalidador ?? ''],
      obligatorio: [c.obligatorio ?? false],
      divClass: [c.divClass ?? 'col-sm-12'],
      nombreVariableDefecto: [c.nombreVariableDefecto ?? ''],
      nombreCampoDefecto: [c.nombreCampoDefecto ?? ''],
      listado: [c.listado ?? ''],
      // Mantenemos el código original del listado al cargar para que el
      // back lo use como referencia al actualizar opciones (ver
      // ProcedimientoService.guardarCampoPestProcCustom).
      listadoSeleccionado: [c.listadoSeleccionado ?? c.listado ?? ''],
      tituloEnfatizado: [c.tituloEnfatizado ?? ''],
      opciones
    });
  }

  emitClose(saved: boolean = false): void {
    this.close.emit({ saved });
  }

  private loadDepartamentos(): void {
    this.servicioGeneral.getDepartamentos().subscribe({
      next: (data) => {
        this.departamentos = this.normalizeDepartamentos(data?.['departamentos']);
      },
      error: () => {
        this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.DEPT_FAIL'), 5000);
      }
    });
  }

  usarDatosDemo(): void {
    const demoDepartamentoId = this.departamentos[0]?.id ?? null;
    this.tramiteForm.patchValue({
      nombre: 'Trámite de demostración',
      codigo: 'DEMO-001',
      sia: '123456',
      identificador: '987654',
      visible: true,
      link_ayuda: 'https://emvs.es/ayuda/demo',
      link_ordenanza: 'https://emvs.es/ordenanza/demo',
      departamento: { id: demoDepartamentoId }
    });
  }

  get previewData() {
    const v = this.tramiteForm?.value ?? {};
    const depId = v.departamento?.id;
    const dep = this.departamentos.find(d => d.id === depId);
    return {
      nombre: v.nombre || '',
      codigo: v.codigo || '',
      sia: v.sia || '',
      identificador: v.identificador || '',
      departamentoNombre: dep?.nombre || '',
      visible: !!v.visible,
      linkAyuda: v.link_ayuda || '',
      linkOrdenanza: v.link_ordenanza || '',
      fechaApertura: v.fechaApertura || '',
      fechaCierre: v.fechaCierre || ''
    };
  }

  get pestanias(): FormArray {
    return this.tramiteForm.get('pestanias') as FormArray;
  }

  get canAdvanceFromStep0(): boolean {
    const f = this.tramiteForm;
    return !!f.get('nombre')?.value
      && !!f.get('codigo')?.value
      && !!f.get('departamento.id')?.value;
  }

  goToStep(step: number): void {
    if (step < 0 || step > 2 || step === this.currentStep) return;
    // avanzar requiere datos válidos del paso 0; retroceder es libre
    if (step > this.currentStep && !this.canAdvanceFromStep0) return;
    this.currentStep = step as BuilderStep;
    if (step !== 1) this.clearSelection();
  }

  nextStep(): void {
    if (this.currentStep < 2) this.goToStep(this.currentStep + 1);
  }

  prevStep(): void {
    if (this.currentStep > 0) this.goToStep(this.currentStep - 1);
  }

  isStepActive(step: number): boolean {
    return this.currentStep === step;
  }

  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  setPreviewMode(mode: PreviewMode): void {
    this.previewMode = mode;
  }

  // ---------- Pestañas ----------

  pestaniaAt(index: number): FormGroup {
    return this.pestanias.at(index) as FormGroup;
  }

  camposOf(pestaniaIndex: number): FormArray {
    return this.pestaniaAt(pestaniaIndex).get('campos') as FormArray;
  }

  campoAt(pestaniaIndex: number, campoIndex: number): FormGroup {
    return this.camposOf(pestaniaIndex).at(campoIndex) as FormGroup;
  }

  camposOfAsGroups(pestaniaIndex: number): FormGroup[] {
    return this.camposOf(pestaniaIndex).controls as FormGroup[];
  }

  asFormGroup(ctrl: AbstractControl): FormGroup {
    return ctrl as FormGroup;
  }

  addPestania(): void {
    const ordenMax = this.pestanias.length + 1;
    const newPestania = this.fb.group({
      id: [null],
      nombre: [this.t('FORM_BUILDER.TAB_N', { n: ordenMax }), Validators.required],
      titulo: [''],
      tagId: [`pest_${ordenMax}`, [Validators.required, Validators.pattern('^[A-z0-9_]{1,255}$')]],
      orden: [ordenMax, Validators.required],
      visible: [true],
      campos: this.fb.array([])
    });
    this.pestanias.push(newPestania);
    const newIndex = this.pestanias.length - 1;
    this.expandedPestanias.add(newIndex);
    this.selectPestania(newIndex);
  }

  deletePestania(index: number): void {
    const pestId = this.pestaniaAt(index).get('id')?.value;
    if (pestId != null) {
      // El back borra en cascada los campos de la pestaña (orphanRemoval),
      // así que basta con encolar la baja de la propia pestaña.
      this.pestaniasEliminadasIds.push(pestId);
    }
    this.pestanias.removeAt(index);
    this.expandedPestanias.delete(index);
    // reindexar el Set
    const shifted = new Set<number>();
    this.expandedPestanias.forEach(i => {
      if (i > index) shifted.add(i - 1);
      else if (i < index) shifted.add(i);
    });
    this.expandedPestanias = shifted;
    const maxIdx = Math.max(this.pestanias.length - 1, 0);
    if (this.previewActiveTabIndex > maxIdx) this.previewActiveTabIndex = maxIdx;
    this.clearSelection();
  }

  // ---------- Copiar pestaña desde otro trámite ----------

  copiandoPestania = false;
  /** Trámites dinámicos disponibles como origen de la copia. */
  tramitesOrigen: any[] = [];
  tramiteOrigenId: number | null = null;
  /** Pestañas (JSON crudo del back) del trámite origen seleccionado. */
  pestaniasOrigen: any[] = [];
  cargandoPestaniasOrigen = false;
  /** Índices de pestañas origen desplegadas para previsualizar sus campos. */
  expandedPestaniasOrigen = new Set<number>();

  openCopiarPestania(): void {
    this.copiandoPestania = true;
    this.tramiteOrigenId = null;
    this.pestaniasOrigen = [];
    this.expandedPestaniasOrigen.clear();
    if (this.tramitesOrigen.length === 0) {
      this.procedimientosService.getProcedimientos().subscribe({
        next: (data) => {
          this.tramitesOrigen = (data?.['Procedimiento'] ?? [])
            .filter((p: any) => p.custom && p.fechaBaja == null)
            .sort((a: any, b: any) => (a.nombre ?? '').localeCompare(b.nombre ?? ''));
        },
        error: () => {
          this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.LIST_FAIL'), 5000);
        }
      });
    }
  }

  closeCopiarPestania(): void {
    this.copiandoPestania = false;
  }

  onTramiteOrigenSelected(id: string): void {
    const idNum = Number(id);
    if (!idNum) return;
    this.tramiteOrigenId = idNum;
    this.pestaniasOrigen = [];
    this.expandedPestaniasOrigen.clear();
    this.cargandoPestaniasOrigen = true;
    this.procedimientosService.getProcedimiento(idNum).subscribe({
      next: (data: any) => {
        this.cargandoPestaniasOrigen = false;
        const proc = data?.['Procedimiento'];
        this.pestaniasOrigen = [...(proc?.pestanias ?? [])]
          .sort((a: any, b: any) => (a.orden ?? 0) - (b.orden ?? 0));
      },
      error: () => {
        this.cargandoPestaniasOrigen = false;
        this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.SELECTED_FAIL'), 5000);
      }
    });
  }

  toggleOrigenExpand(index: number): void {
    if (this.expandedPestaniasOrigen.has(index)) {
      this.expandedPestaniasOrigen.delete(index);
    } else {
      this.expandedPestaniasOrigen.add(index);
    }
  }

  isOrigenExpanded(index: number): boolean {
    return this.expandedPestaniasOrigen.has(index);
  }

  camposOrdenadosDe(p: any): any[] {
    return [...(p?.campos ?? [])].sort((a: any, b: any) => (a.orden ?? 0) - (b.orden ?? 0));
  }

  copiarPestaniaOrigen(p: any): void {
    const group = this.buildPestaniaGroup(p, true);
    group.get('orden')?.setValue(this.pestanias.length + 1);
    group.get('tagId')?.setValue(this.tagIdUnico(group.get('tagId')?.value || 'pest'));
    this.pestanias.push(group);
    const idx = this.pestanias.length - 1;
    this.expandedPestanias.add(idx);
    this.selectPestania(idx);
    this.closeCopiarPestania();
    const nCampos = (p.campos ?? []).length;
    this.notificationService.success(
      'Pestaña copiada',
      `«${p.nombre}» añadida con ${nCampos} campo(s). Se creará al guardar el trámite.`,
      5000
    );
  }

  /** Evita colisiones de tagId dentro del trámite añadiendo sufijo _copia. */
  private tagIdUnico(base: string): string {
    const existentes = new Set(this.pestanias.controls.map(c => c.get('tagId')?.value));
    if (!existentes.has(base)) return base;
    let candidato = `${base}_copia`;
    let i = 2;
    while (existentes.has(candidato)) candidato = `${base}_copia${i++}`;
    return candidato;
  }

  togglePestaniaExpand(index: number): void {
    if (this.expandedPestanias.has(index)) {
      this.expandedPestanias.delete(index);
    } else {
      this.expandedPestanias.add(index);
    }
  }

  isPestaniaExpanded(index: number): boolean {
    return this.expandedPestanias.has(index);
  }

  // ---------- Campos ----------

  addCampo(pestaniaIndex: number): void {
    const campos = this.camposOf(pestaniaIndex);
    const ordenMax = campos.length + 1;
    const newCampo = this.fb.group({
      id: [null],
      tipo: ['text', Validators.required],
      label: [`Campo ${ordenMax}`],
      textoRepresentativo: [''],
      nombre: [`campo_${ordenMax}`, Validators.required],
      valor: [''],
      orden: [ordenMax, Validators.required],
      visible: [true],
      habilitado: [true],
      tipovalidador: [''],
      patternvalidador: [''],
      obligatorio: [false],
      divClass: ['col-sm-12'],
      nombreVariableDefecto: [''],
      nombreCampoDefecto: [''],
      listado: [''],
      listadoSeleccionado: [''],
      tituloEnfatizado: [''],
      opciones: this.fb.array([])
    });
    campos.push(newCampo);
    this.expandedPestanias.add(pestaniaIndex);
    this.selectCampo(pestaniaIndex, campos.length - 1);
  }

  deleteCampo(pestaniaIndex: number, campoIndex: number): void {
    const campos = this.camposOf(pestaniaIndex);
    const campoId = (campos.at(campoIndex) as FormGroup).get('id')?.value;
    if (campoId != null) this.camposEliminadosIds.push(campoId);
    campos.removeAt(campoIndex);
    this.clearSelection();
  }

  deleteSelectedPestania(): void {
    if (this.selection.pestaniaIndex !== null) {
      this.deletePestania(this.selection.pestaniaIndex);
    }
  }

  deleteSelectedCampo(): void {
    const { pestaniaIndex, campoIndex } = this.selection;
    if (pestaniaIndex !== null && campoIndex !== null) {
      this.deleteCampo(pestaniaIndex, campoIndex);
    }
  }

  getCampoTipo(pestaniaIndex: number, campoIndex: number): string {
    return this.campoAt(pestaniaIndex, campoIndex).get('tipo')?.value ?? '';
  }

  getTipoAbbr(tipo: string): string {
    return (this.translate.instant('FORM_BUILDER.TIPO.' + tipo) || tipo).slice(0, 4);
  }

  // ---------- Selección ----------

  selectPestania(pestaniaIndex: number): void {
    this.selection = { pestaniaIndex, campoIndex: null };
    this.previewActiveTabIndex = pestaniaIndex;
  }

  selectCampo(pestaniaIndex: number, campoIndex: number): void {
    this.selection = { pestaniaIndex, campoIndex };
    this.expandedPestanias.add(pestaniaIndex);
    this.previewActiveTabIndex = pestaniaIndex;
  }

  clearSelection(): void {
    this.selection = { pestaniaIndex: null, campoIndex: null };
  }

  isPestaniaSelected(index: number): boolean {
    return this.selection.pestaniaIndex === index && this.selection.campoIndex === null;
  }

  isCampoSelected(pestaniaIndex: number, campoIndex: number): boolean {
    return this.selection.pestaniaIndex === pestaniaIndex
      && this.selection.campoIndex === campoIndex;
  }

  get selectedPestania(): FormGroup | null {
    if (this.selection.pestaniaIndex === null) return null;
    return this.pestaniaAt(this.selection.pestaniaIndex);
  }

  get selectedCampo(): FormGroup | null {
    if (this.selection.pestaniaIndex === null || this.selection.campoIndex === null) return null;
    return this.campoAt(this.selection.pestaniaIndex, this.selection.campoIndex);
  }

  get selectionMode(): 'none' | 'pestania' | 'campo' {
    if (this.selectedCampo) return 'campo';
    if (this.selectedPestania) return 'pestania';
    return 'none';
  }

  // ---------- Tipo de campo ----------

  setCampoTipo(tipo: string): void {
    const campo = this.selectedCampo;
    if (!campo) return;
    campo.get('tipo')?.setValue(tipo);
    // limpiar campos incompatibles
    if (!this.showValidation(tipo)) {
      campo.get('tipovalidador')?.setValue('');
      campo.get('patternvalidador')?.setValue('');
    }
    if (!this.showPrecarga(tipo)) {
      campo.get('nombreVariableDefecto')?.setValue('');
      campo.get('nombreCampoDefecto')?.setValue('');
    }
    if (!this.showOpciones(tipo)) {
      (campo.get('opciones') as FormArray).clear();
      campo.get('listado')?.setValue('');
    }
  }

  showValidation(tipo: string): boolean {
    return !['select', 'multiselect', 'checkbox', 'file'].includes(tipo);
  }

  showPrecarga(tipo: string): boolean {
    return !['multiselect', 'checkbox', 'file'].includes(tipo);
  }

  showOpciones(tipo: string): boolean {
    return ['select', 'multiselect'].includes(tipo);
  }

  // ---------- Opciones (select/multiselect) ----------

  get opcionesDelCampoSeleccionado(): FormArray | null {
    return this.selectedCampo?.get('opciones') as FormArray | null;
  }

  addOpcion(): void {
    const opciones = this.opcionesDelCampoSeleccionado;
    if (!opciones) return;
    if (!this.nuevaOpcion.valor.trim() || !this.nuevaOpcion.label.trim()) return;
    opciones.push(this.fb.group({
      id: [null],
      valor: [this.nuevaOpcion.valor],
      label: [this.nuevaOpcion.label],
      idBaseDatos: [null]
    }));
    this.nuevaOpcion = { valor: '', label: '' };
  }

  removeOpcion(index: number): void {
    this.opcionesDelCampoSeleccionado?.removeAt(index);
  }

  // ---------- Drag & drop ----------

  get connectedCamposLists(): string[] {
    return this.pestanias.controls.map((_, i) => `campos-list-${i}`);
  }

  onCampoDrop(event: CdkDragDrop<FormGroup[]>, targetPestaniaIndex: number): void {
    const targetCampos = this.camposOf(targetPestaniaIndex);
    if (event.previousContainer === event.container) {
      moveItemInArray(targetCampos.controls, event.previousIndex, event.currentIndex);
    } else {
      const sourcePestaniaIndex = parseInt(
        event.previousContainer.id.replace('campos-list-', ''), 10);
      if (isNaN(sourcePestaniaIndex)) return;
      const sourceCampos = this.camposOf(sourcePestaniaIndex);
      transferArrayItem(
        sourceCampos.controls,
        targetCampos.controls,
        event.previousIndex,
        event.currentIndex
      );
    }
    // reasignar orden en ambas pestañas afectadas
    this.recomputeOrdenCampos(targetCampos);
    if (event.previousContainer !== event.container) {
      const sourcePestaniaIndex = parseInt(
        event.previousContainer.id.replace('campos-list-', ''), 10);
      this.recomputeOrdenCampos(this.camposOf(sourcePestaniaIndex));
    }
    this.clearSelection();
  }

  private recomputeOrdenCampos(campos: FormArray): void {
    campos.controls.forEach((ctrl, i) => ctrl.get('orden')?.setValue(i + 1));
  }

  onPestaniaDrop(event: CdkDragDrop<FormGroup[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.pestanias.controls, event.previousIndex, event.currentIndex);
    this.pestanias.updateValueAndValidity();
    this.recomputeOrdenPestanias();

    // Remapear todo el estado auxiliar que referencia pestañas por índice.
    const remap = (i: number) =>
      this.remapIndexAfterMove(i, event.previousIndex, event.currentIndex);
    const shifted = new Set<number>();
    this.expandedPestanias.forEach(i => shifted.add(remap(i)));
    this.expandedPestanias = shifted;
    if (this.selection.pestaniaIndex !== null) {
      this.selection = { ...this.selection, pestaniaIndex: remap(this.selection.pestaniaIndex) };
    }
    this.previewActiveTabIndex = remap(this.previewActiveTabIndex);
  }

  private recomputeOrdenPestanias(): void {
    this.pestanias.controls.forEach((ctrl, i) => ctrl.get('orden')?.setValue(i + 1));
  }

  /** Índice resultante de `index` tras mover un elemento de `from` a `to`. */
  private remapIndexAfterMove(index: number, from: number, to: number): number {
    if (index === from) return to;
    if (from < to && index > from && index <= to) return index - 1;
    if (to < from && index >= to && index < from) return index + 1;
    return index;
  }

  // ---------- Preview ----------

  selectPreviewTab(index: number): void {
    this.previewActiveTabIndex = index;
  }

  isPreviewTabActive(index: number): boolean {
    return index === this.previewActiveTabIndex;
  }

  openExpandedPreview(): void {
    this.previewExpanded = true;
  }

  closeExpandedPreview(): void {
    this.previewExpanded = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewExpanded) this.closeExpandedPreview();
    if (this.copiandoPestania) this.closeCopiarPestania();
  }

  opcionesOfCampo(campo: AbstractControl): FormGroup[] {
    const arr = (campo as FormGroup).get('opciones') as FormArray | null;
    return (arr?.controls ?? []) as FormGroup[];
  }

  buildExportObject(): ProcedimientoDinamico {
    const v = this.tramiteForm?.value ?? {};
    const depId = v.departamento?.id;
    const dep = this.departamentos.find(d => d.id === depId);

    const pestanias: Pestania[] = (v.pestanias ?? []).map((p: any, pIdx: number) => ({
      id: p.id ?? undefined,
      nombre: p.nombre,
      tagId: p.tagId,
      titulo: p.titulo,
      orden: p.orden ?? pIdx + 1,
      visible: p.visible ?? true,
      campos: (p.campos ?? []).map((c: any, cIdx: number): Campo => ({
        id: c.id ?? undefined,
        tipo: c.tipo,
        label: c.label,
        nombre: c.nombre,
        textoRepresentativo: c.textoRepresentativo || undefined,
        tituloEnfatizado: c.tituloEnfatizado || undefined,
        obligatorio: !!c.obligatorio,
        habilitado: c.habilitado ?? true,
        visible: c.visible ?? true,
        tipovalidador: c.tipovalidador || undefined,
        patternvalidador: c.patternvalidador || undefined,
        divClass: c.divClass || 'col-sm-12',
        nombreVariableDefecto: c.nombreVariableDefecto || undefined,
        nombreCampoDefecto: c.nombreCampoDefecto || undefined,
        listado: c.listado || undefined,
        // IMPORTANTE: NO enviamos listadoSeleccionado para que el back-end use
        // siempre la rama "link existing" en ProcedimientoService.guardarCampoPestProcCustom
        // y nunca la rama de UPDATE/DELETE que provocaba el borrado masivo de
        // opciones. La gestión de listados se hace en la pantalla dedicada.
        orden: c.orden ?? cIdx + 1,
        opcionesListado: (c.opciones ?? []).map((op: any) => ({
          id: op.valor,
          label: op.label,
          idBaseDatos: op.idBaseDatos ?? undefined
        }))
      }))
    }));

    return {
      id: v.id ?? undefined,
      nombre: v.nombre,
      codigo: v.codigo,
      sia: v.sia,
      identificador: v.identificador,
      visible: this.ocultarVisible ? false : !!v.visible,
      link_ayuda: v.link_ayuda,
      link_ordenanza: v.link_ordenanza,
      fechaApertura: v.fechaApertura,
      fechaCierre: v.fechaCierre,
      departamento: dep,
      custom: true,
      pestanias
    };
  }

  get previewJson(): string {
    return JSON.stringify(this.buildExportObject(), null, 2);
  }

  copyPreviewJson(): void {
    const text = this.previewJson;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.jsonCopied = true;
        setTimeout(() => this.jsonCopied = false, 2000);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); this.jsonCopied = true; } catch {}
      document.body.removeChild(ta);
      setTimeout(() => this.jsonCopied = false, 2000);
    }
  }

  downloadPreviewJson(): void {
    const text = this.previewJson;
    const codigo = this.tramiteForm.get('codigo')?.value || 'tramite';
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tramite-${codigo}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ---------- Guardado ----------

  async saveTramite(): Promise<void> {
    if (this.saving) return;
    if (this.tramiteForm.invalid) {
      this.tramiteForm.markAllAsTouched();
      this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.REQUIRED'), 5000);
      return;
    }
    const camposSinListado = this.findCamposSelectSinListado();
    if (camposSinListado.length > 0) {
      this.notificationService.error(
        this.t('COMMON.ERROR'),
        this.t('FORM_BUILDER.MSG.REQUIRED'),
        6000
      );
      return;
    }
    this.saving = true;

    try {
      const proc = this.buildExportObject();
      const procResp: any = await firstValueFrom(
        this.procedimientosService.guardaProcedimiento(proc)
      );
      const idProc: number | undefined = procResp?.id ?? proc.id;
      if (!idProc) throw new Error('El backend no devolvió id del procedimiento');

      this.tramiteForm.get('id')?.setValue(idProc);

      // Bajas pendientes: primero campos sueltos, después pestañas completas
      // (el back cascada el borrado de los campos de una pestaña eliminada).
      // Se retiran de la cola según se confirman para que un reintento tras
      // error no repita bajas ya aplicadas.
      for (const campoId of [...this.camposEliminadosIds]) {
        await firstValueFrom(
          this.procedimientosService.bajaCampoPestaniaProcedimiento(
            { id: campoId, opcionesListado: [] } as Campo
          )
        );
        this.camposEliminadosIds = this.camposEliminadosIds.filter(id => id !== campoId);
      }
      for (const pestId of [...this.pestaniasEliminadasIds]) {
        await firstValueFrom(
          this.procedimientosService.bajaPestaniaProcedimiento(
            { id: pestId, campos: [] } as Pestania
          )
        );
        this.pestaniasEliminadasIds = this.pestaniasEliminadasIds.filter(id => id !== pestId);
      }

      for (const pest of proc.pestanias ?? []) {
        const pestResp: any = await firstValueFrom(
          this.procedimientosService.guardaPestaniaProcedimiento(idProc, pest)
        );
        const idPest: number | undefined = pestResp?.id ?? pest.id;
        if (!idPest) continue;

        for (const campo of pest.campos ?? []) {
          await firstValueFrom(
            this.procedimientosService.guardaCampoPestaniaProcedimiento(idProc, idPest, campo)
          );
        }
      }

      this.saved = true;
      this.notificationService.success(this.t('COMMON.SAVED'), this.t('FORM_BUILDER.MSG.SAVED'), 5000);
    } catch (err) {
      this.notificationService.error(this.t('COMMON.ERROR'), this.t('FORM_BUILDER.MSG.SAVE_FAIL'), 5000);
    } finally {
      this.saving = false;
    }
  }

  resetBuilder(): void {
    this.saved = false;
    this.currentStep = 0;
    this.selection = { pestaniaIndex: null, campoIndex: null };
    this.expandedPestanias.clear();
    this.previewActiveTabIndex = 0;
    this.pestaniasEliminadasIds = [];
    this.camposEliminadosIds = [];
    this.tramiteForm.reset({
      id: null,
      nombre: '',
      codigo: '',
      sia: null,
      identificador: null,
      visible: false,
      link_ayuda: '',
      link_ordenanza: '',
      fechaApertura: '',
      fechaCierre: '',
      departamento: { id: null },
      custom: true
    });
    while (this.pestanias.length) this.pestanias.removeAt(0);
  }

  private normalizeDepartamentos(raw: unknown): Departamento[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item: any) => ({
        id: item?.id ?? item?.Id,
        nombre: String(item?.nombre ?? item?.Nombre ?? '').trim(),
        departamentos: item?.departamentos ?? []
      }))
      .filter((dep) => dep.id != null && dep.nombre.length > 0);
  }
}
